import {match} from 'ts-pattern';

import {isNotNil} from 'ramda';

import type {Nullish} from 'shared/web';

import type {FinancialProductOfferDimensions} from '../schemas/FinancialProductOfferDimensionsSchema';
import type {FinancialProductPreferenceCoordinates} from '../schemas/FinancialProductPreferenceCoordinatesSchema';
import type {FinancialProductSelection} from '../schemas/FinancialProductSelectionSchema';
import {constrainFinancingSelection} from './constrainFinancingSelection';
import {getFinancingControlLimits} from './getFinancingControlLimits';

const offeredOrStored = <T>(stored: T | Nullish, offered: T[]) =>
  // When nothing is offered any more, the stored value remains the record of what was chosen.
  isNotNil(stored) && offered.includes(stored) ? stored : (offered.at(0) ?? stored ?? null);

/** A down payment remains valid between brackets and is clamped only when that range moves. */
const withinDownPaymentBrackets = (stored: number | Nullish, brackets: number[]) => {
  const lowest = brackets.at(0);
  const highest = brackets.at(-1);

  if (!isNotNil(stored)) {
    return lowest ?? null;
  }

  if (!isNotNil(lowest) || !isNotNil(highest)) {
    return stored;
  }

  return Math.min(Math.max(stored, lowest), highest);
};

/** Prefer the stored leasing cell, then the same term, before falling back to any offered cell. */
const offeredLeasingCell = (
  offer: FinancialProductOfferDimensions,
  stored: FinancialProductSelection | null
) => {
  const cells = offer.availableLeasingCells;
  const storedCell = cells.find(
    (cell) => cell.termMonths === stored?.termMonths && cell.annualMileage === stored?.annualMileage
  );

  return (
    storedCell ??
    cells.find((cell) => cell.termMonths === stored?.termMonths) ??
    cells.at(0) ??
    (isNotNil(stored) ? {termMonths: stored.termMonths, annualMileage: stored.annualMileage} : null)
  );
};

/**
 * With per-term cells, the term is kept if it still has cells, then the balloon and the down
 * payment are rebased onto that term's own brackets rather than onto the union lists.
 */
const offeredFinancingCoordinates = (
  offer: FinancialProductOfferDimensions,
  stored: FinancialProductSelection | null
) => {
  const isBalloon = offer.productType === 'BALLOON';
  const terms = getFinancingControlLimits(offer, {
    termMonths: null,
    downPaymentPercentage: null,
    balloonPaymentPercentage: null,
  }).terms;
  const constrained = constrainFinancingSelection(offer, {
    termMonths: offeredOrStored(stored?.termMonths, terms),
    downPaymentPercentage: stored?.downPaymentPercentage ?? null,
    balloonPaymentPercentage: isBalloon ? (stored?.balloonPaymentPercentage ?? null) : null,
  });

  return {
    termMonths: constrained.termMonths,
    downPaymentPercentage: constrained.downPaymentPercentage,
    balloonPaymentPercentage: isBalloon
      ? constrained.balloonPaymentPercentage
      : offeredOrStored(stored?.balloonPaymentPercentage, offer.availableBalloonPaymentPercentages),
  };
};

/** Rebase a stored choice onto today's offered configuration without saving it. */
export const toFinancialProductPreferenceCoordinates = (
  offer: FinancialProductOfferDimensions,
  options?: {fallbackSelection?: FinancialProductSelection | null}
): FinancialProductPreferenceCoordinates => {
  const selection = offer.selection ?? options?.fallbackSelection ?? null;

  return match(offer.productType)
    .with('LEASING', () => {
      const cell = offeredLeasingCell(offer, selection);

      return {
        productType: offer.productType,
        termMonths: cell?.termMonths ?? null,
        downPaymentPercentage: null,
        balloonPaymentPercentage: null,
        leasingTargetGroup: offeredOrStored(
          selection?.leasingTargetGroup,
          offer.availableTargetGroups
        ),
        annualMileage: cell?.annualMileage ?? null,
      };
    })
    .otherwise(() => ({
      productType: offer.productType,
      ...(isNotNil(offer.availableFinancingCells)
        ? offeredFinancingCoordinates(offer, selection)
        : {
            termMonths: offeredOrStored(selection?.termMonths, offer.availableTerms),
            downPaymentPercentage: withinDownPaymentBrackets(
              selection?.downPaymentPercentage,
              offer.availableDownPaymentPercentages
            ),
            balloonPaymentPercentage: offeredOrStored(
              selection?.balloonPaymentPercentage,
              offer.availableBalloonPaymentPercentages
            ),
          }),
      leasingTargetGroup: null,
      annualMileage: null,
    }));
};
